import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";
import api from "@/api";
import { formatCurrency } from "@/utils/formatters";

const DEFAULT_TIMEFRAME = "1m";
const TIMEFRAME_STORAGE_KEY = "portfolio_graph_timeframe";
const CHART_MODE_STORAGE_KEY = "portfolio_graph_mode";
const DISPLAY_UNIT_STORAGE_KEY = "portfolio_graph_unit";
// Deterministic palette used to color per-account lines. Picked to be
// distinguishable in both light and dark themes; cycles if there are more
// accounts than colors.
const ACCOUNT_LINE_COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#EC4899", "#14B8A6", "#F97316",
];
const VALID_TIMEFRAMES = [
  "1w",
  "1m",
  "3m",
  "6m",
  "ytd",
  "1y",
  "5y",
  "10y",
  "all",
];

const getInitialTimeframe = () => {
  if (typeof window === "undefined") return DEFAULT_TIMEFRAME;

  const saved = window.localStorage.getItem(TIMEFRAME_STORAGE_KEY);
  return VALID_TIMEFRAMES.includes(saved) ? saved : DEFAULT_TIMEFRAME;
};

const getInitialChartMode = () => {
  if (typeof window === "undefined") return "overall";
  const saved = window.localStorage.getItem(CHART_MODE_STORAGE_KEY);
  return saved === "per_account" ? "per_account" : "overall";
};

const getInitialDisplayUnit = () => {
  if (typeof window === "undefined") return "dollar";
  const saved = window.localStorage.getItem(DISPLAY_UNIT_STORAGE_KEY);
  return saved === "percent" ? "percent" : "dollar";
};

const PortfolioGraphWidget = ({
  accountId,
  refreshTrigger = 0,
  perfData = null,
}) => {
  const [timeframe, setTimeframe] = useState(getInitialTimeframe);
  // Per-account mode only applies when viewing all accounts; coerce to "overall"
  // for single-account views so toggling the URL doesn't bleed state.
  const [chartMode, setChartMode] = useState(() =>
    accountId === null ? getInitialChartMode() : "overall",
  );
  const [displayUnit, setDisplayUnit] = useState(getInitialDisplayUnit);
  const [chartData, setChartData] = useState([]);
  const [accountSeries, setAccountSeries] = useState([]); // [{name, color}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (accountId !== null && chartMode !== "overall") setChartMode("overall");
  }, [accountId, chartMode]);

  useEffect(() => {
    if (accountId === null && typeof window !== "undefined") {
      window.localStorage.setItem(CHART_MODE_STORAGE_KEY, chartMode);
    }
  }, [chartMode, accountId]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DISPLAY_UNIT_STORAGE_KEY, displayUnit);
    }
  }, [displayUnit]);

  const timeframes = [
    { label: "1W", value: "1w" },
    { label: "1M", value: "1m" },
    { label: "3M", value: "3m" },
    { label: "6M", value: "6m" },
    { label: "YTD", value: "ytd" },
    { label: "1Y", value: "1y" },
    { label: "5Y", value: "5y" },
    { label: "10Y", value: "10y" },
    { label: "ALL", value: "all" },
  ];

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(TIMEFRAME_STORAGE_KEY, timeframe);
    }
  }, [timeframe]);

  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Calculate date range based on timeframe
        const endDate = new Date();
        let startDate = new Date();

        switch (timeframe) {
          case "1d":
            startDate.setDate(endDate.getDate() - 1);
            break;
          case "1w":
            startDate.setDate(endDate.getDate() - 7);
            break;
          case "1m":
            startDate.setMonth(endDate.getMonth() - 1);
            break;
          case "3m":
            startDate.setMonth(endDate.getMonth() - 3);
            break;
          case "6m":
            startDate.setMonth(endDate.getMonth() - 6);
            break;
          case "ytd":
            // Start from January 2nd to avoid including Dec 31 of previous year
            startDate = new Date(endDate.getFullYear(), 0, 2);
            break;
          case "1y":
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
          case "5y":
            startDate.setFullYear(endDate.getFullYear() - 5);
            break;
          case "10y":
            startDate.setFullYear(endDate.getFullYear() - 10);
            break;
          case "all":
            // Don't set start date to get all data
            startDate = null;
            break;
          default:
            startDate.setMonth(endDate.getMonth() - 1);
        }

        // Build query params - format dates in LOCAL timezone, not UTC
        const formatLocalDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        const params = {
          end_date: formatLocalDate(endDate),
        };
        if (startDate) {
          params.start_date = formatLocalDate(startDate);
        }

        const parseLocalDate = (dateStr) => {
          const [year, month, day] = dateStr.split("-");
          return new Date(year, month - 1, day);
        };

        if (chartMode === "per_account") {
          // All-accounts multi-line: one Line per account, wide-format dataset
          // keyed by the account name (sanitized).
          const response = await api.get(`/accounts/all/snapshots/by-account`, { params });
          const accounts = response.data || [];

          // Build {date -> {accountName: value}} then flatten to a sorted array.
          const dateMap = new Map();
          accounts.forEach((acc) => {
            (acc.snapshots || []).forEach((snap) => {
              const value = Number(snap.total_value);
              if (!(value > 10)) return; // skip zero/garbage
              if (!dateMap.has(snap.date)) dateMap.set(snap.date, { date: snap.date });
              dateMap.get(snap.date)[acc.account_name] = value;
            });
          });

          const wide = Array.from(dateMap.values())
            .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

          const series = accounts.map((acc, i) => ({
            name: acc.account_name,
            color: ACCOUNT_LINE_COLORS[i % ACCOUNT_LINE_COLORS.length],
          }));

          setChartData(wide);
          setAccountSeries(series);
        } else {
          // Single-line: account total or summed portfolio.
          const url = accountId
            ? `/accounts/${accountId}/snapshots`
            : `/accounts/all/snapshots`;
          const response = await api.get(url, { params });

          const formattedData = response.data
            .map((snapshot) => ({
              date: snapshot.date,
              value: snapshot.total_value,
              rawDate: parseLocalDate(snapshot.date),
            }))
            .filter((snapshot) => snapshot.value > 10)
            .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

          setChartData(formattedData);
          setAccountSeries([]);
        }
      } catch (err) {
        console.error("Error fetching portfolio graph data:", err);
        setError("Failed to load chart data");
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [accountId, timeframe, refreshTrigger, chartMode]);

  // Actual span of the data on the chart (not the requested timeframe).
  // YTD on an account opened in February shows ~3 months of data, not 12 — the
  // x-axis labels need to reflect that or the chart looks empty/mysterious.
  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split("-");
    return new Date(y, m - 1, d);
  };

  const spanDays = useMemo(() => {
    if (chartData.length < 2) return 0;
    const first = parseLocalDate(chartData[0].date);
    const last = parseLocalDate(chartData[chartData.length - 1].date);
    return Math.max(0, (last - first) / (1000 * 60 * 60 * 24));
  }, [chartData]);

  // Pick label granularity based on actual span. The y-axis already conveys
  // dollar/percent magnitude; the x-axis just needs to tell the reader where
  // they are in time without overflowing.
  const formatDateForTimeframe = (dateStr) => {
    const date = parseLocalDate(dateStr);
    if (spanDays === 0) {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    if (spanDays <= 365) {
      // Up to a year: "Mar 12" — needed for short spans where month-only
      // collapses everything onto one or two labels.
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    if (spanDays <= 365 * 3) {
      // 1–3 years: "Mar '26"
      return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    }
    // >3 years: just the year.
    return date.toLocaleDateString("en-US", { year: "numeric" });
  };

  // Tick density driven by actual span — also driven by actual span. The
  // first and last data points are always rendered as ticks so the reader can
  // see exactly where the visible range starts and ends.
  const getFilteredTicks = () => {
    if (chartData.length === 0) return undefined;
    if (chartData.length === 1) return [chartData[0].date];

    let intervalDays;
    if (spanDays <= 14) intervalDays = 2;
    else if (spanDays <= 45) intervalDays = 7;
    else if (spanDays <= 120) intervalDays = 14;
    else if (spanDays <= 365) intervalDays = 30;
    else if (spanDays <= 365 * 3) intervalDays = 90;
    else if (spanDays <= 365 * 10) intervalDays = 180;
    else intervalDays = 365;

    const ticks = [chartData[0].date];
    let lastTickDate = parseLocalDate(chartData[0].date);

    for (let i = 1; i < chartData.length - 1; i++) {
      const d = parseLocalDate(chartData[i].date);
      if ((d - lastTickDate) / (1000 * 60 * 60 * 24) >= intervalDays) {
        ticks.push(chartData[i].date);
        lastTickDate = d;
      }
    }

    // Always anchor the right edge with the last point — but if the last
    // interior tick is too close, replace it instead of doubling up labels.
    const lastDateStr = chartData[chartData.length - 1].date;
    const lastDate = parseLocalDate(lastDateStr);
    const gap = (lastDate - lastTickDate) / (1000 * 60 * 60 * 24);
    if (gap >= intervalDays * 0.5) {
      ticks.push(lastDateStr);
    } else if (ticks.length > 1) {
      ticks[ticks.length - 1] = lastDateStr;
    } else {
      ticks.push(lastDateStr);
    }

    return ticks;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!(active && payload && payload.length)) return null;
    const data = payload[0].payload;
    const [year, month, day] = data.date.split("-");
    const date = new Date(year, month - 1, day);

    return (
      <div className="bg-gray-800 dark:bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-lg">
        <p className="text-sm text-gray-300 mb-1">
          {date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </p>
        {chartMode === "per_account" ? (
          payload
            .filter((p) => p.value != null)
            .map((p) => (
              <p key={p.dataKey} className="text-sm text-white flex items-center gap-2">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: p.color }}
                />
                <span className="text-gray-300">{p.dataKey}:</span>
                <span className="font-semibold">{formatChartValue(p.value)}</span>
              </p>
            ))
        ) : (
          <p className="text-base font-semibold text-white">
            {formatChartValue(data.value)}
          </p>
        )}
      </div>
    );
  };

  const getPerformanceColor = () => {
    if (chartData.length < 2) return "#3B82F6";

    const firstValue = chartData[0].value;
    const lastValue = chartData[chartData.length - 1].value;

    if (lastValue >= firstValue) {
      return "#10B981"; // green
    } else {
      return "#EF4444"; // red
    }
  };

  const calculatePerformance = () => {
    // Use server-side performance data when available — it uses correct trading day
    // boundaries and strips contributions, matching the Performance section numbers.
    if (perfData) {
      const periodMap = { "1d": "daily", "1w": "weekly", "1m": "monthly" };
      const periodKey = periodMap[timeframe];
      if (periodKey && perfData[periodKey]) {
        const p = perfData[periodKey];
        return { change: p.total_pnl, changePercent: p.pnl_percent };
      }
    }

    if (chartData.length < 2) return null;

    const firstValue = chartData[0].value;
    const lastValue = chartData[chartData.length - 1].value;

    // Ensure we have valid positive values (filter should handle this, but double-check)
    if (!firstValue || firstValue <= 0 || !lastValue) return null;

    const change = lastValue - firstValue;
    const changePercent = (change / firstValue) * 100;

    return { change, changePercent };
  };

  const performance = calculatePerformance();

  // Data rebased to percent change from the first visible point. Each series
  // is normalized independently in per-account mode so accounts of different
  // sizes can be compared.
  const displayData = useMemo(() => {
    if (displayUnit !== "percent" || chartData.length === 0) return chartData;

    if (chartMode === "per_account") {
      const firstByAccount = {};
      accountSeries.forEach((s) => {
        const firstRow = chartData.find((r) => typeof r[s.name] === "number");
        if (firstRow) firstByAccount[s.name] = firstRow[s.name];
      });
      return chartData.map((row) => {
        const out = { date: row.date };
        accountSeries.forEach((s) => {
          const v = row[s.name];
          const base = firstByAccount[s.name];
          if (typeof v === "number" && base && base !== 0) {
            out[s.name] = ((v - base) / base) * 100;
          }
        });
        return out;
      });
    }

    const base = chartData[0].value;
    if (!base) return chartData;
    return chartData.map((d) => ({ ...d, value: ((d.value - base) / base) * 100 }));
  }, [chartData, displayUnit, chartMode, accountSeries]);

  const formatYAxisTick = (value) => {
    if (displayUnit === "percent") {
      const sign = value > 0 ? "+" : "";
      return `${sign}${value.toFixed(1)}%`;
    }
    const sign = value < 0 ? "-" : "";
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}k`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  const formatChartValue = (value) => {
    if (displayUnit === "percent") {
      const sign = value > 0 ? "+" : "";
      return `${sign}${value.toFixed(2)}%`;
    }
    return formatCurrency(value);
  };

  // Calculate Y-axis domain to zoom into the actual data range
  const getYAxisDomain = () => {
    if (displayData.length === 0) return [0, "auto"];

    const values = [];
    if (chartMode === "per_account") {
      displayData.forEach((row) => {
        accountSeries.forEach((s) => {
          const v = row[s.name];
          if (typeof v === "number") values.push(v);
        });
      });
    } else {
      displayData.forEach((d) => values.push(d.value));
    }
    if (values.length === 0) return [0, "auto"];

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;

    const padding = range * 0.1;
    // In dollar mode, never go below 0 (portfolio values are nonneg).
    // In percent mode, the range can legitimately straddle zero.
    const yMin = displayUnit === "percent" ? minValue - padding : Math.max(0, minValue - padding);
    const yMax = maxValue + padding;

    return [yMin, yMax];
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Account Value
          </h3>
          {performance && chartMode !== "per_account" && (
            <div className="flex items-baseline gap-2 mt-1">
              <span
                className={`text-lg font-semibold ${
                  performance.change >= 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {performance.change >= 0 ? "+" : ""}
                {formatCurrency(performance.change)}
              </span>
              <span
                className={`text-sm ${
                  performance.change >= 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                ({performance.changePercent >= 0 ? "+" : ""}
                {performance.changePercent.toFixed(2)}%)
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* $ / % toggle — applies in both single- and all-accounts views */}
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {[
              { value: "dollar", label: "$" },
              { value: "percent", label: "%" },
            ].map((unit) => (
              <button
                key={unit.value}
                onClick={() => setDisplayUnit(unit.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  displayUnit === unit.value
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
                aria-label={unit.value === "dollar" ? "Show dollar values" : "Show percent change"}
              >
                {unit.label}
              </button>
            ))}
          </div>

          {/* Mode selector — only meaningful for all-accounts view */}
          {accountId === null && (
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              {[
                { value: "overall", label: "Overall" },
                { value: "per_account", label: "Per account" },
              ].map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => setChartMode(mode.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    chartMode === mode.value
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          )}

          {/* Timeframe selector */}
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {timeframes.map((tf) => (
              <button
                key={tf.value}
                onClick={() => setTimeframe(tf.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  timeframe === tf.value
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      ) : chartData.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            No historical data available
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Portfolio snapshots will be created automatically each day
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={displayData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#374151"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              stroke="#9CA3AF"
              style={{ fontSize: "12px" }}
              tickLine={false}
              ticks={getFilteredTicks()}
              tickFormatter={(date) => formatDateForTimeframe(date)}
              minTickGap={20}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#9CA3AF"
              style={{ fontSize: "12px" }}
              tickLine={false}
              domain={getYAxisDomain()}
              tickFormatter={formatYAxisTick}
            />
            <Tooltip content={<CustomTooltip />} />
            {displayUnit === "percent" && (
              <ReferenceLine
                y={0}
                yAxisId="right"
                stroke="#9CA3AF"
                strokeDasharray="3 3"
                strokeWidth={1}
              />
            )}
            {/* Dollar mode: ground line at the period's starting value so
                gain/loss is visually obvious. Skipped in per-account mode
                where each line would need its own baseline (too noisy). */}
            {displayUnit === "dollar" &&
              chartMode !== "per_account" &&
              chartData.length > 0 && (
                <ReferenceLine
                  y={chartData[0].value}
                  yAxisId="right"
                  stroke="#9CA3AF"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
              )}
            {chartMode === "per_account" ? (
              <>
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                {accountSeries.map((s) => (
                  <Line
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    yAxisId="right"
                    connectNulls
                  />
                ))}
              </>
            ) : (
              <Line
                type="monotone"
                dataKey="value"
                stroke={getPerformanceColor()}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6 }}
                yAxisId="right"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default PortfolioGraphWidget;
